import { curveStep } from 'd3-shape';

import { areaSeries, lineSeries, scatterSeries } from './panelgraph/components/series';
import {
	formatPressure,
	formatRainfall,
	formatTemperature,
	formatWindGust,
	formatWindSpeed,
	getDateTimeFormatConverter
} from './formatters';
import { yAxis } from './panelgraph/components/axis-y';
import { panel } from './panelgraph/components/panel';
import { convertWindBearing16 } from './convert-wind-bearing';
import { xAxis } from './panelgraph/components/axis-x';
import { plotLine } from './panelgraph/components/plot-line';
import { plotBand } from './panelgraph/components/plot-band';
import { GRAPH_VIEW, PRESSURE_UNIT, RAIN_UNIT } from './constants';
import { convertPressure, convertRainfall, convertTemperature, convertWindSpeed } from './unit-converters';
import {
	getLowestMaxRainAxisValue,
	getLowestMaxWindAxisValue,
	getPressureAxisStepOptions,
	getRainAxisStepOptions,
	getWindAxisStepOptions
} from './graph-limits';

export function addSunLinesAndBands(data, plotLines, plotBands) {
	// If sun events are present in the data, create plotlines and plotbands for these.
	if (data.sunEvents) {

		data.sunEvents.forEach(sunEvent => {
			plotLines.push(plotLine(sunEvent.type)
				.label(sunEvent.type === 'sunrise' ? 'Sunrise' : 'Sunset')
				.x(sunEvent.time));
		});

		const numSunEvents = data.sunEvents.length;

		if (numSunEvents > 0) {

			// Night time band from data start until first sunrise
			if (data.sunEvents[0].type === 'sunrise') {
				plotBands.push(plotBand('night')
					.range([null, data.sunEvents[0].time]));
			}

			for (let i = 1; i < numSunEvents; i++) {
				if (data.sunEvents[i].type === 'sunrise') {
					plotBands.push(plotBand('night')
						.range([data.sunEvents[i - 1].time, data.sunEvents[i].time]));
				}
			}

			// Night time band from last sunset until data end
			if (data.sunEvents[numSunEvents - 1].type === 'sunset') {
				plotBands.push(plotBand('night')
					.range([data.sunEvents[numSunEvents - 1].time, null]));
			}

		}

	}
}

export function addDstLines(data, plotLines) {
	// Add plotlines for any DST events present
	if (data.dstEvents) {

		data.dstEvents.forEach(dstEvent => {
			plotLines.push(plotLine('dst')
				.label(dstEvent.type === 'start' ? 'DST start' : 'DST end')
				.x(dstEvent.time));
		});

	}
}

export function addDateChangeLines(data, plotLines) {
	// If date changes are present, add plotlines for these
	if (data.dateChanges) {

		data.dateChanges.forEach(dateChange => {
			plotLines.push(plotLine('dateChange')
				.x(dateChange.time));
		});
	}
}

export function xFormatter24h(data) {
	return tickTime => {
		const tick = data.axis.filter(tick => tick.time === tickTime)[0];
		const hour = Number(tick.timeString.split(' ')[1].split(':')[0]);

		if (hour === 0) {
			return '12am';
		}

		if (hour < 12) {
			return hour + 'am';
		}

		if (hour === 12) {
			return '12pm';
		}

		return (hour - 12) + 'pm';
	};
}

export function xFormatter7d(data) {
	const dateFormatter = getDateTimeFormatConverter('%Y-%m-%d %H:%M:%S', '%-d %b');

	return tickTime => {
		const tick = data.axis.filter(tick => tick.time === tickTime)[0];
		return dateFormatter(tick.timeString);
	};
}

export function xFormatterMonth(data) {
	const dateFormatter = getDateTimeFormatConverter('%Y-%m-%d %H:%M:%S', '%e');

	return tickTime => {
		const tick = data.axis.filter(tick => tick.time === tickTime)[0];
		return dateFormatter(tick.timeString);
	};
}

export function tooltipTimeFormatter(format) {
	return (function(formatter) {
		return pt => formatter(pt.timestamp);
	})(
		getDateTimeFormatConverter('%Y-%m-%d %H:%M:%S', format)
	);
}

export function tooltipTimeFormatter24h() {
	return tooltipTimeFormatter('%-I:%M %p');
}

export function tooltipTimeFormatterMultiDay() {
	return tooltipTimeFormatter('%-d %b %-I:%M %p');
}

export function getTimeAxis(data, tickValues, xFormatter, tooltipFormatter) {
	return xAxis()
		.data(pt => pt.time)
		.range(data.range)
		.tooltipFormatter(tooltipFormatter)
		.tickValues(tickValues)
		.formatter(xFormatter);
}

export function chartHeight(graphView) {
	switch (graphView) {

		case GRAPH_VIEW.COMBINED:
			return w => Math.max(0.8 * w, 640);

		case GRAPH_VIEW.WIND:
			return w => Math.max(0.5 * w, 300);

		default:
			return w => Math.max(0.5 * w, 200);
	}
}

export function windSpeedPanel(speedUnit, curveFn, useHourlyHighGust = false) {

	const windSpeedSeries = areaSeries('windspeed')
		.data(pt => pt.windSpeed === null
			? null
			: [
				0,
				convertWindSpeed(pt.windSpeed, speedUnit)
			]
		)
		.curve(curveFn)
		.label(`Wind Speed ${speedUnit}`)
		.tooltipFormatter(pt => formatWindSpeed(pt.windSpeed, speedUnit) + ' ' + speedUnit);

	const gustKey = useHourlyHighGust ? 'hourlyHighWindGust' : 'windGust';

	const windGustSeries = areaSeries('windgust')
		.data(pt => pt[gustKey] === null
			? null
			: [
				convertWindSpeed(pt.windSpeed, speedUnit),
				convertWindSpeed(pt[gustKey], speedUnit)
			]
		)
		.curve(curveFn)
		.label(`Gust ${speedUnit}`)
		.tooltipFormatter(pt => formatWindGust(pt[gustKey], speedUnit) + ' ' + speedUnit);

	const speedAxis = yAxis()
		.formatter(v => Math.round(v))
		.constraints({
			fixedMinValue: 0,
			lowestMaxValue: getLowestMaxWindAxisValue(speedUnit),
			stepOptions: getWindAxisStepOptions(speedUnit)
		});

	return panel()
		.series([ windSpeedSeries, windGustSeries ])
		.axisDual(speedAxis);

}

export function windDirectionPanel() {

	const windDirectionSeries = scatterSeries('winddirection')
		.data(pt => pt.windBearing)
		.label('Wind Bearing')
		.tooltipFormatter(pt => convertWindBearing16(pt.windBearing) + ` (${pt.windBearing}°)`);

	const directionLabels = {
		0: 'N',
		90: 'E',
		180: 'S',
		270: 'W',
		360: 'N'
	};

	const bearingAxis = yAxis()
		.tickValues([0, 90, 180, 270, 360])
		.formatter(
			value => directionLabels[value],
			value => value
		);

	return panel()
		.series([ windDirectionSeries ])
		.axisDual(bearingAxis);

}

export function temperaturePanel(tempUnit, curveFn) {

	const temperatureSeries = lineSeries('temperature')
		.data(pt => pt.temperature === null ? null : convertTemperature(pt.temperature, tempUnit))
		.curve(curveFn)
		.label(`Temperature °${tempUnit}`)
		.tooltipFormatter(pt => formatTemperature(pt.temperature, tempUnit) + `°${tempUnit}`);

	const dewpointSeries = lineSeries('dewpoint')
		.data(pt => pt.dewpoint === null ? null : convertTemperature(pt.dewpoint, tempUnit))
		.curve(curveFn)
		.label(`Dewpoint °${tempUnit}`)
		.tooltipFormatter(pt => formatTemperature(pt.dewpoint, tempUnit) + `°${tempUnit}`);

	const temperatureAxis = yAxis()
		.formatter(v => Math.round(v))
		.constraints({
			stepOptions: [1, 2, 3, 4, 5, 10]
		});

	return panel()
		.series([ temperatureSeries, dewpointSeries ])
		.axisDual(temperatureAxis);

}

export function rainfallPanel(rainUnit, label = 'Daily Rainfall', key = 'dailyRainfall') {

	const rainSeries = areaSeries('rainfall')
		.curve(curveStep)
		.data(pt => pt[key] === null
			? null
			: [0, convertRainfall(pt[key], rainUnit)]
		)
		.label(`${label} ${rainUnit}`)
		.tooltipFormatter(pt => formatRainfall(pt[key], rainUnit) + ' ' + rainUnit);

	const rainAxis = yAxis()
		.formatter(v => rainUnit === RAIN_UNIT.IN ? Math.round(v * 10) / 10 : Math.round(v))
		.constraints({
			fixedMinValue: 0,
			lowestMaxValue: getLowestMaxRainAxisValue(rainUnit),
			stepOptions: getRainAxisStepOptions(rainUnit)
		});

	return panel()
		.series([ rainSeries ])
		.axisDual(rainAxis);

}

export function hourlyRainfallPanel(rainUnit) {

	const hourlyRainSeries = areaSeries('hourlyrainfall')
		.curve(curveStep)
		.data(pt => pt.hourlyRainfall === null
			? null
			: [0, convertRainfall(pt.hourlyRainfall, rainUnit)]
		)
		.label(`Hourly Rainfall ${rainUnit}`)
		.tooltipFormatter(pt => formatRainfall(pt.hourlyRainfall, rainUnit) + ' ' + rainUnit);

	const hourlyRainAxis = yAxis()
		.formatter(v => rainUnit === RAIN_UNIT.IN ? Math.round(v * 10) / 10 : Math.round(v))
		.constraints({
			fixedMinValue: 0,
			lowestMaxValue: getLowestMaxRainAxisValue(rainUnit),
			stepOptions: getRainAxisStepOptions(rainUnit)
		});

	return panel()
		.series([ hourlyRainSeries ])
		.axisDual(hourlyRainAxis);

}

export function rainRatePanel(rainUnit) {

	const rainrateSeries = areaSeries('rainrate')
		.curve(curveStep)
		.data(pt => pt.meanRainRate === null
			? null
			: [0, convertRainfall(pt.meanRainRate, rainUnit)]
		)
		.label(`Mean Rain Rate ${rainUnit}/hr`)
		.tooltipFormatter(pt => formatRainfall(pt.meanRainRate, rainUnit) + ' ' + rainUnit + '/hr');

	const rainrateAxis = yAxis()
		.formatter(v => rainUnit === RAIN_UNIT.IN ? Math.round(v * 10) / 10 : Math.round(v))
		.constraints({
			fixedMinValue: 0,
			lowestMaxValue: getLowestMaxRainAxisValue(rainUnit),
			stepOptions: getRainAxisStepOptions(rainUnit)
		});

	return panel()
		.series([ rainrateSeries ])
		.axisDual(rainrateAxis);

}

export function pressureHumidityPanel(pressureUnit, curveFn) {

	const pressureSeries = lineSeries('pressure')
		.data(pt => pt.pressure === null
			? null
			: convertPressure(pt.pressure, pressureUnit)
		)
		.label(`Pressure ${pressureUnit}`)
		.side('left')
		.curve(curveFn)
		.tooltipFormatter(pt => formatPressure(pt.pressure, pressureUnit) + ' ' + pressureUnit);

	const humiditySeries = lineSeries('humidity')
		.data(pt => pt.humidity)
		.label('Humidity %')
		.side('right')
		.curve(curveFn)
		.tooltipFormatter(pt => pt.humidity + '%');

	// primary y axis is pressure
	const pressureAxis = yAxis(true)
		.formatter(v => pressureUnit === PRESSURE_UNIT.INHG ? v.toFixed(1) : Math.round(v))
		.constraints({
			stepOptions: getPressureAxisStepOptions(pressureUnit)
		});

	// secondary axis is humidity - the calculated tick values will be subject to the additional
	// constraint of having an identical number of ticks as the primary axis
	const humidityAxis = yAxis()
		.formatter(v => Math.round(v))
		.constraints({
			lowestMinValue: 0,
			highestMaxValue: 100,
			stepOptions: [5, 10, 15, 20, 25, 50]
		});

	return panel()
		.series([ pressureSeries, humiditySeries ])
		.axisLeft(pressureAxis)
		.axisRight(humidityAxis);

}

export function pressurePanel(pressureUnit, curveFn) {

	const pressureSeries = lineSeries('pressure')
		.data(pt => pt.pressure === null
			? null
			: convertPressure(pt.pressure, pressureUnit)
		)
		.label(`Pressure ${pressureUnit}`)
		.curve(curveFn)
		.tooltipFormatter(pt => formatPressure(pt.pressure, pressureUnit) + ' ' + pressureUnit);

	const pressureAxis = yAxis()
		.formatter(v => pressureUnit === PRESSURE_UNIT.INHG ? v.toFixed(1) : Math.round(v))
		.constraints({
			stepOptions: getPressureAxisStepOptions(pressureUnit)
		});

	return panel()
		.series([ pressureSeries ])
		.axisDual(pressureAxis);

}

export function humidityPanel(curveFn) {

	const humiditySeries = lineSeries('humidity')
		.data(pt => pt.humidity)
		.label('Humidity %')
		.curve(curveFn)
		.tooltipFormatter(pt => pt.humidity + '%');

	const humidityAxis = yAxis()
		.formatter(v => Math.round(v))
		.constraints({
			lowestMinValue: 0,
			highestMaxValue: 100,
			stepOptions: [5, 10, 15, 20, 25, 50]
		});

	return panel()
		.series([ humiditySeries ])
		.axisDual(humidityAxis);

}



// WEBPACK FOOTER //
// ./utils/observations-graph-components.js